/**
 * CountryPickerModal
 * Reusable modal for selecting a country (onboarding + profile).
 *
 * Split into Content + Modal wrapper so Storybook can render
 * the content directly without a native Modal overlay.
 */

import React, { useMemo, useCallback } from 'react';
import {
  Modal,
  FlatList,
  Pressable,
  TouchableOpacity,
  View,
} from 'react-native';
import { Box, HStack, Text } from '@gluestack-ui/themed';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { getCountryDisplayName } from '../i18n';
import { useThemeColors, colors, FS_XS, FS_MD, FS_LG } from '../theme';
import { buildCountryList } from '../data/countryConfig';
import type { CountryLocaleConfig } from '../data/countryConfig';
import { useDisplayTheme } from '../hooks/useDisplayTheme';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CountryPickerModalContentProps {
  detectedCountry: string | undefined;
  selectedCountry: string | null;
  countries: CountryLocaleConfig[];
  onSelect: (code: string) => void;
  onClose: () => void;
  /** Renders without modal backdrop — for Storybook */
  embedded?: boolean;
}

export interface CountryPickerModalProps {
  visible: boolean;
  detectedCountry: string | undefined;
  selectedCountry: string | null;
  countries: CountryLocaleConfig[];
  onSelect: (code: string) => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Row component (memo for FlatList perf)
// ---------------------------------------------------------------------------

interface CountryRowProps {
  country: CountryLocaleConfig;
  isSelected: boolean;
  label: string;
  onPress: (code: string) => void;
  theme: ReturnType<typeof useThemeColors>;
  fontScale: number;
  ffs: { fontFamily: string } | undefined;
}

const CountryRow = React.memo(function CountryRow({
  country,
  isSelected,
  label,
  onPress,
  theme,
  fontScale,
  ffs,
}: CountryRowProps) {
  const handlePress = useCallback(() => onPress(country.code), [onPress, country.code]);

  return (
    <TouchableOpacity
      testID={`country-picker-row-${country.code}`}
      accessibilityLabel={label}
      accessibilityRole="button"
      accessibilityState={{ selected: isSelected }}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <HStack
        space="md"
        p="$3"
        style={{
          alignItems: 'center',
          backgroundColor: isSelected
            ? (theme.isDark ? '#1E3A5F' : '#EBF5FF')
            : 'transparent',
          borderRadius: 10,
        }}
      >
        <Text allowFontScaling={false} style={{ fontSize: 22 }}>
          {country.emoji}
        </Text>
        <Text
          allowFontScaling={false}
          style={{
            fontSize: FS_MD * fontScale,
            fontWeight: isSelected ? '600' : '400',
            color: isSelected ? colors.primary : theme.text,
            flex: 1,
            ...ffs,
          }}
        >
          {label}
        </Text>
        {isSelected && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_MD * fontScale, color: colors.primary }}
          >
            ✓
          </Text>
        )}
      </HStack>
    </TouchableOpacity>
  );
});

// ---------------------------------------------------------------------------
// Content (exported for Storybook)
// ---------------------------------------------------------------------------

export function CountryPickerModalContent({
  detectedCountry,
  selectedCountry,
  countries,
  onSelect,
  onClose,
  embedded = false,
}: CountryPickerModalContentProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const insets = useSafeAreaInsets();

  const { suggested, rest } = useMemo(
    () => buildCountryList(detectedCountry, countries),
    [detectedCountry, countries],
  );

  const getLabel = useCallback(
    (code: string) => getCountryDisplayName(code, i18n.language),
    [],
  );

  // Build flat data array with section headers for FlatList
  type ListItem =
    | { type: 'header'; title: string; key: string }
    | { type: 'divider'; key: string }
    | { type: 'country'; country: CountryLocaleConfig; key: string };

  const listData = useMemo<ListItem[]>(() => {
    const items: ListItem[] = [];

    if (suggested.length > 0) {
      items.push({ type: 'header', title: t('countryPicker.suggested'), key: 'header-suggested' });
      for (const c of suggested) {
        items.push({ type: 'country', country: c, key: c.code });
      }
      if (rest.length > 0) {
        items.push({ type: 'divider', key: 'divider' });
      }
    }

    const sortedRest = [...rest].sort((a, b) => {
      const labelA = getLabel(a.code);
      const labelB = getLabel(b.code);
      return labelA.localeCompare(labelB);
    });

    for (const c of sortedRest) {
      items.push({ type: 'country', country: c, key: c.code });
    }

    return items;
  }, [suggested, rest, t, getLabel]);

  const renderItem = useCallback(
    ({ item }: { item: ListItem }) => {
      if (item.type === 'header') {
        return (
          <Text
            allowFontScaling={false}
            accessibilityRole="header"
            style={{
              fontSize: FS_XS * fontScale,
              fontWeight: '600',
              color: theme.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              paddingHorizontal: 12,
              paddingTop: 8,
              paddingBottom: 4,
              ...ffs,
            }}
          >
            {item.title}
          </Text>
        );
      }

      if (item.type === 'divider') {
        return (
          <View
            style={{
              height: 1,
              backgroundColor: theme.border,
              marginVertical: 4,
              marginHorizontal: 12,
            }}
          />
        );
      }

      return (
        <CountryRow
          country={item.country}
          isSelected={selectedCountry === item.country.code}
          label={getLabel(item.country.code)}
          onPress={onSelect}
          theme={theme}
          fontScale={fontScale}
          ffs={ffs}
        />
      );
    },
    [selectedCountry, onSelect, theme, fontScale, ffs, getLabel],
  );

  const keyExtractor = useCallback((item: ListItem) => item.key, []);

  const card = (
    <Box
      style={{
        backgroundColor: theme.card,
        borderTopLeftRadius: embedded ? 16 : 20,
        borderTopRightRadius: embedded ? 16 : 20,
        borderBottomLeftRadius: embedded ? 16 : 0,
        borderBottomRightRadius: embedded ? 16 : 0,
        height: embedded ? undefined : '85%',
        flex: embedded ? 1 : undefined,
      }}
    >
      {/* Header */}
      <HStack
        p="$4"
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottomWidth: 1,
          borderBottomColor: theme.border,
        }}
      >
        <Text
          allowFontScaling={false}
          accessibilityRole="header"
          style={{
            fontSize: FS_LG * fontScale,
            fontWeight: '700',
            color: theme.text,
            flex: 1,
            ...ffs,
          }}
        >
          {t('countryPicker.title')}
        </Text>
        <TouchableOpacity
          testID="country-picker-close"
          accessibilityLabel={t('common.cancel')}
          accessibilityRole="button"
          onPress={onClose}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Text
            allowFontScaling={false}
            style={{
              fontSize: FS_LG * fontScale,
              color: theme.textSecondary,
              fontWeight: '300',
            }}
          >
            ✕
          </Text>
        </TouchableOpacity>
      </HStack>

      {/* Country list */}
      <FlatList
        data={listData}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ paddingHorizontal: 8, paddingBottom: Math.max(24, insets.bottom) }}
        showsVerticalScrollIndicator={false}
      />
    </Box>
  );

  if (embedded) return card;

  return (
    <Box
      flex={1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      justifyContent="flex-end"
    >
      {/* Tapping outside the card dismisses the picker */}
      <Pressable
        testID="country-picker-backdrop"
        onPress={onClose}
        style={{ flex: 1 }}
      />
      {card}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Modal wrapper
// ---------------------------------------------------------------------------

export function CountryPickerModal({
  visible,
  detectedCountry,
  selectedCountry,
  countries,
  onSelect,
  onClose,
}: CountryPickerModalProps) {
  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      <CountryPickerModalContent
        detectedCountry={detectedCountry}
        selectedCountry={selectedCountry}
        countries={countries}
        onSelect={onSelect}
        onClose={onClose}
      />
    </Modal>
  );
}
